import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import { SelfServeCSV } from './types';
import {
  downloadOriginalCsv,
  downloadResultCsv,
} from 'services/self-serve-magic-link';

type Props = {
  csv: SelfServeCSV,
  type: 'original' | 'result',
};

function downloadURI(uri, name) {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const generateResultFilenameFromOriginal = (originalName: string) => {
  const arr = originalName.split('.');
  const ext = arr.pop();
  return `${arr.join('.')}_result.${ext}`;
};

const nameRenderer = (csv: SelfServeCSV, type: 'original' | 'result') => {
  if (type === 'original') return csv.originalName;
  return generateResultFilenameFromOriginal(csv.originalName);
};

export default function SSMagicLinkCSVDownloader({ csv, type }: Props) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const handleDownloadCsvFile = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { link } = await queryClient.fetchQuery({
        queryKey: [
          type === 'original'
            ? 'downloadSSMagicLinkCSVOriginal'
            : 'downloadSSMagicLinkCSVResult',
          { id: csv.id },
        ],
        queryFn: () => {
          if (type === 'original') return downloadOriginalCsv(csv.id);
          return downloadResultCsv(csv.id);
        },
      });
      downloadURI(link, nameRenderer(csv, type));
    } catch (e) {
      // Ignore error
    }
    setLoading(false);
  }, [queryClient, csv.id, csv.originalname, loading]);
  return (
    <div className="space-x-2 flex items-center">
      {loading && <Spin size="small" spinning={false} />}
      <a aria-disabled={loading} href="#" onClick={handleDownloadCsvFile}>
        {nameRenderer(csv, type)}
      </a>
    </div>
  );
}
